import type { ExperienceTrackerAPI } from '@atlassian/experience-tracker/ExperienceTracker';

import { ExperienceName } from './constants';
import { getRemixFeatureArea } from './remix-feature-area';

/**
 * A short registration window lets sibling embed effects register before a page
 * with fast-loading embeds is declared successful.
 */
export const REMIX_VIEW_SETTLE_DELAY_MS = 100;

export type RemixViewAttributes = Record<string, unknown> & {
	contentType?: string;
	featureArea?: string;
	pageMode?: string;
	product?: string;
	remixType?: string;
};

type EmbedOutcome = 'pending' | 'success' | 'fail';

type EmbedRegistration = {
	attributes: RemixViewAttributes;
	mounted: boolean;
	outcome: EmbedOutcome;
};

type PageSession = {
	embeds: Map<string, EmbedRegistration>;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionId?: string;
	pageSessionKey: object;
	settleTimer?: ReturnType<typeof setTimeout>;
	stopped: boolean;
};

/**
 * The page session key is supplied by the host's shared experience-tracker
 * context. `useAIMateExperienceTracker()` returns a distinct Proxy per hook, so
 * using the reporting tracker itself as this key would incorrectly create one
 * session per embed.
 */
const sessions = new Map<object, PageSession>();

const getPageSessionKey = (
	experienceTracker: ExperienceTrackerAPI,
	pageSessionKey?: object,
): object => pageSessionKey ?? experienceTracker;

const clearSettleTimer = (session: PageSession): void => {
	if (session.settleTimer !== undefined) {
		clearTimeout(session.settleTimer);
		session.settleTimer = undefined;
	}
};

const distinctStrings = (values: Array<unknown>): string[] => [
	...new Set(
		values.filter((value): value is string => typeof value === 'string' && value.length > 0),
	),
];

const buildPageAttributes = (
	session: PageSession,
	terminalAttributes?: RemixViewAttributes,
): RemixViewAttributes => {
	const registrations = [...session.embeds.values()];
	const remixTypes = distinctStrings(registrations.map(({ attributes }) => attributes.remixType));
	const contentTypes = distinctStrings(
		registrations.map(({ attributes }) => attributes.contentType),
	);
	const pageModes = distinctStrings(registrations.map(({ attributes }) => attributes.pageMode));
	const product = registrations.find(({ attributes }) => attributes.product)?.attributes.product;
	const existingFeatureArea = registrations.find(({ attributes }) => attributes.featureArea)
		?.attributes.featureArea;
	const featureArea = getRemixFeatureArea({
		remixType:
			remixTypes.length === 1 ? remixTypes[0] : remixTypes.length > 1 ? 'mixed' : undefined,
		existingFeatureArea: terminalAttributes?.featureArea ?? existingFeatureArea,
	});

	return {
		...terminalAttributes,
		...(product ? { product } : {}),
		...(contentTypes.length === 1 ? { contentType: contentTypes[0] } : {}),
		...(contentTypes.length > 1 ? { contentType: 'mixed', contentTypes } : {}),
		...(pageModes.length === 1 ? { pageMode: pageModes[0] } : {}),
		...(pageModes.length > 1 ? { pageMode: 'mixed', pageModes } : {}),
		...(remixTypes.length === 1 ? { remixType: remixTypes[0] } : {}),
		...(remixTypes.length > 1 ? { remixType: 'mixed', remixTypes } : {}),
		featureArea,
		embedCount: registrations.length,
	};
};

const maybeDeleteSession = (session: PageSession): void => {
	if ([...session.embeds.values()].every(({ mounted }) => !mounted)) {
		clearSettleTimer(session);
		sessions.delete(session.pageSessionKey);
	}
};

const abortForNavigation = (session: PageSession): void => {
	if (session.stopped) {
		return;
	}

	clearSettleTimer(session);
	session.stopped = true;
	try {
		session.experienceTracker.abort({
			name: ExperienceName.REMIX_VIEW,
			reason: 'User navigated away before Remix embeds finished rendering',
			attributes: buildPageAttributes(session, { abortReason: 'navigation' }),
		});
	} catch {
		// Reliability instrumentation must never surface errors into the host product.
	}
};

const getMatchingSession = (
	experienceTracker: ExperienceTrackerAPI,
	pageSessionKey?: object,
	pageSessionId?: string,
): PageSession | undefined => {
	const session = sessions.get(getPageSessionKey(experienceTracker, pageSessionKey));
	if (
		pageSessionId !== undefined &&
		session?.pageSessionId !== undefined &&
		session.pageSessionId !== pageSessionId
	) {
		return undefined;
	}
	return session;
};

const schedulePageSuccess = (session: PageSession): void => {
	clearSettleTimer(session);
	if (
		session.stopped ||
		session.embeds.size === 0 ||
		[...session.embeds.values()].some(({ outcome }) => outcome === 'pending')
	) {
		return;
	}

	session.settleTimer = setTimeout(() => {
		session.settleTimer = undefined;
		if (
			session.stopped ||
			[...session.embeds.values()].some(({ outcome }) => outcome === 'pending')
		) {
			return;
		}

		session.stopped = true;
		try {
			session.experienceTracker.succeed({
				name: ExperienceName.REMIX_VIEW,
				attributes: buildPageAttributes(session),
			});
		} catch {
			// Reliability instrumentation must never surface errors into the host product.
		} finally {
			maybeDeleteSession(session);
		}
	}, REMIX_VIEW_SETTLE_DELAY_MS);
};

/** Clears all page state without emitting. Exposed for deterministic tests. */
export const resetRemixViewExperience = (): void => {
	sessions.forEach(clearSettleTimer);
	sessions.clear();
};

/**
 * Registers one Remix render as part of the current page-level experience.
 * The first registration starts the parent; later registrations update its
 * final aggregate rather than starting competing experiences with the same name.
 */
export const beginRemixEmbedRender = ({
	attributes = {},
	embedId,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	attributes?: RemixViewAttributes;
	embedId: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const resolvedPageSessionKey = getPageSessionKey(experienceTracker, pageSessionKey);
	let session = sessions.get(resolvedPageSessionKey);
	if (
		session &&
		pageSessionId !== undefined &&
		session.pageSessionId !== undefined &&
		session.pageSessionId !== pageSessionId
	) {
		abortForNavigation(session);
		sessions.delete(resolvedPageSessionKey);
		session = undefined;
	}

	if (!session) {
		session = {
			embeds: new Map(),
			experienceTracker,
			pageSessionId,
			pageSessionKey: resolvedPageSessionKey,
			stopped: false,
		};
		sessions.set(resolvedPageSessionKey, session);
	} else if (session.pageSessionId === undefined && pageSessionId !== undefined) {
		session.pageSessionId = pageSessionId;
	}

	if (session.stopped) {
		return;
	}

	clearSettleTimer(session);
	const existing = session.embeds.get(embedId);
	session.embeds.set(embedId, {
		attributes: { ...existing?.attributes, ...attributes },
		mounted: true,
		outcome: existing?.outcome ?? 'pending',
	});

	if (session.embeds.size === 1 && !existing) {
		experienceTracker.start({
			name: ExperienceName.REMIX_VIEW,
			id: ExperienceName.REMIX_VIEW,
			attributes: buildPageAttributes(session),
		});
		return;
	}

	experienceTracker.updateAttributes({
		name: ExperienceName.REMIX_VIEW,
		attributes: buildPageAttributes(session),
	});
};

/** Marks one registered embed successful and completes the page once all are successful. */
export const completeRemixEmbedRender = ({
	embedId,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	embedId: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const session = getMatchingSession(experienceTracker, pageSessionKey, pageSessionId);
	const registration = session?.embeds.get(embedId);
	if (!session || session.stopped || !registration || registration.outcome !== 'pending') {
		return;
	}

	registration.outcome = 'success';
	schedulePageSuccess(session);
};

/** Fails the page-level experience as soon as any registered Remix embed fails. */
export const failRemixEmbedRender = ({
	attributes,
	embedId,
	error,
	errorType,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	attributes?: RemixViewAttributes;
	embedId: string;
	error: Error;
	errorType?: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const session = getMatchingSession(experienceTracker, pageSessionKey, pageSessionId);
	const registration = session?.embeds.get(embedId);
	if (!session || session.stopped || !registration || registration.outcome !== 'pending') {
		return;
	}

	clearSettleTimer(session);
	registration.attributes = {
		...registration.attributes,
		...attributes,
		...(errorType ? { errorType } : {}),
	};
	registration.outcome = 'fail';
	session.stopped = true;

	try {
		experienceTracker.fail({
			name: ExperienceName.REMIX_VIEW,
			error,
			attributes: buildPageAttributes(session, registration.attributes),
		});
	} catch {
		// Reliability instrumentation must never surface errors into the host product.
	} finally {
		maybeDeleteSession(session);
	}
};

/**
 * Deregisters an embed. Leaving while a render is pending aborts the passive
 * page-load experience so navigation is excluded from the reliability SLI.
 */
export const releaseRemixEmbedRender = ({
	embedId,
	experienceTracker,
	pageSessionKey,
	pageSessionId,
}: {
	embedId: string;
	experienceTracker: ExperienceTrackerAPI;
	pageSessionKey?: object;
	pageSessionId?: string;
}): void => {
	const session = getMatchingSession(experienceTracker, pageSessionKey, pageSessionId);
	const registration = session?.embeds.get(embedId);
	if (!session || !registration) {
		return;
	}

	registration.mounted = false;
	const allEmbedsUnmounted = [...session.embeds.values()].every(({ mounted }) => !mounted);
	const shouldAbort =
		!session.stopped && (registration.outcome === 'pending' || allEmbedsUnmounted);
	try {
		if (shouldAbort) {
			clearSettleTimer(session);
			session.stopped = true;
			experienceTracker.abort({
				name: ExperienceName.REMIX_VIEW,
				reason:
					registration.outcome === 'pending'
						? 'User navigated away before Remix embeds finished rendering'
						: 'User navigated away before the Remix page result was emitted',
				attributes: buildPageAttributes(session, { abortReason: 'navigation' }),
			});
		}
	} catch {
		// Reliability instrumentation must never surface errors into the host product.
	} finally {
		maybeDeleteSession(session);
	}
};
